import { Injectable, Injector } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import {
  Emittable,
  Emitter,
  EmitterAction,
  Receiver,
} from '@ngxs-labs/emitter';
import { Selector, State, StateContext } from '@ngxs/store';
import { catchError, of, switchMap, tap } from 'rxjs';
import { FeedbackDataService } from './services/feedback-data.service';
import { Page } from './types/page.type';
export interface FeedbackModel {
  loading: boolean;
  start: string | null;
  end: string | null;
  partIdFilter: string;
  selectedAiCase?: string;
  feedbackResponse?: any;
  pages: Page[];
  limit: number;
  lastrow: any;
  updatedRaw: any;
  currentPage: number;
  totalPages: number | null;
  targetPage: number | null;
  selectedOptionalColumns: string[];
}

@State<FeedbackModel>({
  name: 'feedback',
  defaults: {
    loading: true,
    partIdFilter: '',
    pages: [{ number: 1, lastrow: {} }],
    start: null,
    end: null,
    limit: 20,
    lastrow: null,
    updatedRaw: null,
    selectedAiCase: '',
    currentPage: 1,
    totalPages: null,
    targetPage: null,
    selectedOptionalColumns: [],
  },
})
@Injectable()
export class FeedbackState {
  static feedbackService: FeedbackDataService;
  @Emitter(FeedbackState.setLoading)
  private static emitLoading: Emittable<void>;
  @Emitter(FeedbackState.setLoadingFinished)
  private static emitLoadingFinished: Emittable<void>;
  @Emitter(FeedbackState.loadFeedback)
  private static emitLoadFeedback: Emittable<void>;
  constructor(injector: Injector) {
    FeedbackState.feedbackService =
      injector.get<FeedbackDataService>(FeedbackDataService);
  }
  @Selector()
  static loading(state: FeedbackModel): boolean {
    return state.loading;
  }

  @Selector()
  static limit(state: FeedbackModel): number {
    return state.limit;
  }

  @Selector()
  static updatedRaw(state: FeedbackModel): string[] {
    return state.updatedRaw;
  }
  @Selector()
  static pages(state: FeedbackModel): Page[] {
    return state.pages;
  }

  @Selector()
  static currentPage(state: FeedbackModel): number {
    return state.currentPage;
  }

  @Selector()
  static totalPages(state: FeedbackModel): number | null {
    return state.totalPages;
  }

  @Selector()
  static selectedOptionalColumns(state: FeedbackModel): string[] {
    return state.selectedOptionalColumns;
  }

  @Selector()
  static feedbackResponse(state: FeedbackModel): any | undefined {
    return state.feedbackResponse;
  }

  @Receiver()
  public static setDateRange(
    { patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<DateRange<string> | null>
  ) {
    const start = payload?.start;
    const end = payload?.end;

    patchState({
      start: start,
      end: end,
      lastrow: null,
      pages: [{ number: 1, lastrow: {} }],
      currentPage: 1,
      totalPages: null,
      targetPage: null,
    });
    return this.emitLoadFeedback.emit();
  }

  @Receiver()
  public static setUpdatedRaw(
    { patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<any>
  ) {
    patchState({ updatedRaw: payload });
  }
  @Receiver()
  public static setPartIdFilter(
    { patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<string>
  ) {
    patchState({
      partIdFilter: payload,
      lastrow: null,
      pages: [{ number: 1, lastrow: {} }],
      currentPage: 1,
      totalPages: null,
      targetPage: null,
    });
    return this.emitLoadFeedback.emit();
  }

  @Receiver()
  public static setLimit(
    { patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<number>
  ) {
    patchState({ limit: payload });
    return this.emitLoadFeedback.emit();
  }

  @Receiver()
  public static setSelectedOptionalColumns(
    { patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<string[]>
  ) {
    patchState({ selectedOptionalColumns: payload });
  }

  @Receiver()
  public static selectAiCase(
    { patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<string>
  ) {
    patchState({ selectedAiCase: payload });
    return this.emitLoadFeedback.emit();
  }
  @Receiver()
  public static lastrow(
    { patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<any>
  ) {
    patchState({ lastrow: payload });
    return this.emitLoadFeedback.emit();
  }
  @Receiver()
  public static pagesEmitter(
    { patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<Page[]>
  ) {
    patchState({ pages: payload, totalPages: null, targetPage: null });
  }

  @Receiver()
  public static navigateToPage(
    { patchState, getState }: StateContext<FeedbackModel>,
    { payload: target }: EmitterAction<number>
  ) {
    const state = getState();
    if (target === state.currentPage) return;

    if (target === 1) {
      patchState({ targetPage: null, lastrow: null, currentPage: 1 });
    } else if (target < state.currentPage) {
      // Backward — cursor always available for visited pages
      const prev = state.pages.find(p => p.number === target - 1);
      patchState({ targetPage: null, lastrow: prev?.lastrow ?? null, currentPage: target });
    } else {
      // Forward — step one at a time toward target
      const currentEntry = state.pages.find(p => p.number === state.currentPage);
      patchState({
        targetPage: target,
        lastrow: currentEntry?.lastrow ?? null,
        currentPage: state.currentPage + 1,
      });
    }
    return this.emitLoadFeedback.emit();
  }
  @Receiver()
  public static setCurrentPage(
    { patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<number>
  ) {
    patchState({ currentPage: payload });
  }
  @Receiver()
  public static feedbackResponseEmitter(
    { patchState, getState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<any>
  ) {
    const current = getState().feedbackResponse ?? {};
    const incoming = payload && typeof payload === 'object' ? payload : {};
    patchState({
      feedbackResponse: {
        ...current,
        ...incoming,
      },
    });
  }
  @Receiver()
  public static sendFeedbackFormData(
    { getState, patchState }: StateContext<FeedbackModel>,
    { payload }: EmitterAction<any>
  ) {
    console.log('helloooo');
    return this.emitLoading.emit().pipe(
      switchMap(() =>
        this.feedbackService.sendFilledInFeedbackData(
          getState().selectedAiCase!,
          payload,
          getState().start,
          getState().end,
          getState().partIdFilter,
          getState().limit,
          getState().lastrow
        )
      ),
      tap((feedbackResponse) => patchState({ feedbackResponse })),
      switchMap(() => this.emitLoadingFinished.emit())
    );
    //switchMap((feedbackResponse) => this.emitLoadFeedback.emit()),
    // switchMap(() => console.log('hi'))
  }

  @Receiver()
  public static setLoading({ patchState }: StateContext<FeedbackModel>) {
    patchState({ loading: true });
  }

  @Receiver()
  public static setLoadingFinished({
    patchState,
  }: StateContext<FeedbackModel>) {
    patchState({ loading: false });
  }

  @Receiver()
  public static loadFeedback({
    patchState,
    getState,
  }: StateContext<FeedbackModel>) {
    if (!getState().selectedAiCase) {
      return;
    }
    return this.emitLoading.emit().pipe(
      switchMap(() =>
        this.feedbackService.getOpenFeedbackData(
          getState().selectedAiCase!,
          getState().start,
          getState().end,
          getState().partIdFilter,
          getState().limit,
          getState().lastrow
        ).pipe(
          tap((feedbackResponse) => {
            const state = getState();
            const pages = state.pages.map(p => ({ ...p }));
            const currentPage = state.currentPage;

            if (feedbackResponse?.lastrow) {
              const idx = pages.findIndex(p => p.number === currentPage);
              if (idx >= 0) pages[idx] = { ...pages[idx], lastrow: feedbackResponse.lastrow };
              // ensure a sentinel entry exists for the next page
              if (!pages.some(p => p.number === currentPage + 1)) {
                pages.push({ number: currentPage + 1, lastrow: {} });
              }
            } else if (pages.length > 1) {
              pages.pop();
            }

            const pictureCount = feedbackResponse?.picture_count;
            const totalPages = pictureCount != null
              ? Math.max(Math.ceil(pictureCount / state.limit), 1)
              : null;

            const target = state.targetPage;
            if (target !== null && currentPage < target && feedbackResponse?.lastrow) {
              // advance one step toward the target page
              patchState({
                feedbackResponse, pages, totalPages,
                lastrow: feedbackResponse.lastrow,
                currentPage: currentPage + 1,
              });
            } else {
              patchState({ feedbackResponse, pages, totalPages, targetPage: null });
            }
          }),
          catchError((err) => {
            const body = err?.error;
            patchState({
              feedbackResponse: {
                dataframe: [],
                errorCode: body?.errorCode ?? 'UNKNOWN_ERROR',
              },
              targetPage: null,
            });
            return of(null);
          })
        )
      ),
      switchMap(() => {
        const state = getState();
        // continue fetching toward targetPage, or finish
        if (state.targetPage !== null && state.currentPage <= state.targetPage) {
          return this.emitLoadFeedback.emit();
        }
        return this.emitLoadingFinished.emit();
      })
    );
  }
}
